import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import FormInput from "../components/FormInput";
import FormSelect from "../components/FormSelect";
import PrimaryButton from "../components/PrimaryButton";

const AUTH_TOKEN_STORAGE_KEY = "authToken";
const AUTH_ROLE_STORAGE_KEY = "authRole";

const STATUS_FILTER_OPTIONS = [
  { label: "Waiting", value: "Waiting" },
  { label: "Checked out", value: "CheckedOut" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const normalizeRole = (role) => {
  const cleaned = String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, "_");

  if (cleaned === "SUPERADMIN") {
    return "SUPER_ADMIN";
  }
  if (cleaned === "TANNERYADMIN") {
    return "TANNERY_ADMIN";
  }

  return cleaned;
};

const getBaseApiUrl = () => {
  const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
  if (!baseApiUrl) {
    throw new Error("REACT_APP_BASE_API_URL is not configured.");
  }
  return baseApiUrl.replace(/\/+$/, "");
};

const parseResponsePayload = async (response) => {
  const responseText = await response.text();
  if (!responseText) {
    return null;
  }
  try {
    return JSON.parse(responseText);
  } catch {
    return responseText;
  }
};

const parseApiMessage = async (response, fallback) => {
  const text = await response.text();
  if (!text) {
    return fallback;
  }
  try {
    const payload = JSON.parse(text);
    return (
      (typeof payload?.message === "string" && payload.message.trim()) ||
      (typeof payload?.detail === "string" && payload.detail.trim()) ||
      fallback
    );
  } catch {
    return text.trim() || fallback;
  }
};

const extractListAndTotal = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.results)
    ? payload.results
    : [];

  const total =
    payload?.total ??
    payload?.count ??
    payload?.total_count ??
    payload?.meta?.total ??
    payload?.pagination?.total ??
    null;

  const parsedTotal = Number(total);
  return {
    list,
    total: Number.isFinite(parsedTotal) && parsedTotal >= 0 ? parsedTotal : null,
  };
};

const normalizeRow = (raw) => {
  const id = String(raw?.id ?? raw?.token_id ?? "").trim();
  const statusRaw = String(raw?.status ?? "").trim();
  const upper = statusRaw.toUpperCase().replace(/\s+/g, "_");
  const isCheckedOut =
    upper === "CHECKED_OUT" ||
    upper === "CHECKEDOUT" ||
    raw?.checked_out === true ||
    raw?.is_checked_out === true;
  const isWaiting =
    upper === "WAITING" ||
    (!statusRaw && raw?.checked_out === false) ||
    (!statusRaw && !isCheckedOut && raw?.checked_out !== true);

  const canDelete =
    typeof raw?.can_delete === "boolean"
      ? raw.can_delete
      : Boolean(
          (upper === "WAITING" || (!statusRaw && raw?.checked_out === false)) && !isCheckedOut
        );

  const displayStatus =
    statusRaw ||
    (isCheckedOut ? "CheckedOut" : isWaiting ? "Waiting" : "—");

  return {
    id,
    short_code: raw?.short_code ?? "—",
    tannery_name: raw?.tannery_name ?? "—",
    name: raw?.name ?? "—",
    phone_number: raw?.phone_number ?? raw?.phone ?? "—",
    car_plate: raw?.car_plate ?? "—",
    zila: raw?.zila ?? "—",
    elaqa: raw?.elaqa ?? "—",
    status: displayStatus,
    canDelete,
  };
};

const getListErrorMessage = (statusCode, payload) => {
  if (statusCode === 401) {
    return "Your session has expired. Please login again.";
  }
  if (statusCode === 403) {
    return "You are not allowed to view this list.";
  }
  if (typeof payload?.message === "string" && payload.message.trim()) {
    return payload.message;
  }
  if (typeof payload === "string" && payload.trim()) {
    return payload;
  }
  return "Unable to load tokens right now.";
};

const getDeleteErrorMessage = (statusCode, payload) => {
  if (statusCode === 401) {
    return "Your session has expired. Please login again.";
  }
  if (statusCode === 403) {
    return "You are not allowed to delete this token.";
  }
  if (statusCode === 409) {
    return (
      (typeof payload?.message === "string" && payload.message.trim()) ||
      (typeof payload?.detail === "string" && payload.detail.trim()) ||
      "Only waiting tokens can be deleted. This token is already checked out."
    );
  }
  if (statusCode === 404) {
    return "Token was not found. It may have already been removed.";
  }
  if (typeof payload?.message === "string" && payload.message.trim()) {
    return payload.message;
  }
  if (typeof payload === "string" && payload.trim()) {
    return payload;
  }
  return "Unable to delete this token right now.";
};

function SuperAdminTokensPage() {
  const navigate = useNavigate();
  const isSuperAdmin = normalizeRole(localStorage.getItem(AUTH_ROLE_STORAGE_KEY)) === "SUPER_ADMIN";

  const [tanneries, setTanneries] = useState([]);
  const [isLoadingTanneries, setIsLoadingTanneries] = useState(false);
  const [tanneriesError, setTanneriesError] = useState("");

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  /** Tannery and status refetch as soon as you change the dropdown. */
  const [tanneryId, setTanneryId] = useState("");
  const [status, setStatus] = useState("");
  /** Search text in the box; sent as `q` only after Apply or Enter. */
  const [searchDraft, setSearchDraft] = useState("");
  const [appliedQ, setAppliedQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const tanneryOptions = useMemo(
    () => tanneries.map((t) => ({ value: t.id, label: t.name })),
    [tanneries]
  );

  const fetchTanneries = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsLoadingTanneries(true);
      setTanneriesError("");
      const response = await fetch(`${getBaseApiUrl()}/super-admin/tanneries`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(await parseApiMessage(response, "Unable to load tanneries."));
      }

      const payload = await response.json();
      const list = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
      const normalized = list
        .map((item) => ({
          id: String(item?.id ?? item?.tannery_id ?? "").trim(),
          name: String(item?.tannery_name ?? item?.name ?? "").trim(),
        }))
        .filter((item) => item.id && item.name);
      setTanneries(normalized);
    } catch (error) {
      setTanneriesError(error.message || "Unable to load tanneries.");
    } finally {
      setIsLoadingTanneries(false);
    }
  }, [navigate]);

  const fetchTokens = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsLoading(true);
      setListError("");
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(Math.min(100, Math.max(1, pageSize))));
      if (tanneryId.trim()) {
        params.set("tannery_id", tanneryId.trim());
      }
      if (status.trim()) {
        params.set("status", status.trim());
      }
      const q = String(appliedQ ?? "").trim();
      if (q) {
        params.set("q", q);
      }

      const response = await fetch(`${getBaseApiUrl()}/super-admin/tokens?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await parseResponsePayload(response);
      if (!response.ok) {
        throw new Error(getListErrorMessage(response.status, payload));
      }

      const { list, total } = extractListAndTotal(payload || {});
      setRows(list.map(normalizeRow));
      setTotalCount(total);
    } catch (error) {
      setRows([]);
      setTotalCount(null);
      setListError(error.message || "Unable to load tokens right now.");
    } finally {
      setIsLoading(false);
    }
  }, [navigate, page, pageSize, tanneryId, status, appliedQ]);

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }
    void fetchTanneries();
  }, [fetchTanneries, isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }
    void fetchTokens();
  }, [fetchTokens, isSuperAdmin]);

  const totalPages =
    totalCount != null ? Math.max(1, Math.ceil(totalCount / pageSize)) : null;

  const hasNextPage =
    totalPages != null ? page < totalPages : rows.length >= pageSize;

  const hasPrevPage = page > 1;

  const rangeLabel = useMemo(() => {
    if (rows.length === 0) {
      return totalCount != null ? `0 of ${totalCount}` : "No rows on this page";
    }
    const start = (page - 1) * pageSize + 1;
    const end = (page - 1) * pageSize + rows.length;
    if (totalCount != null) {
      return `${start}–${end} of ${totalCount}`;
    }
    return `Rows ${start}–${end} (page ${page})`;
  }, [page, pageSize, rows.length, totalCount]);

  const applyFilters = () => {
    setSuccessMessage("");
    setAppliedQ(String(searchDraft ?? "").trim());
    setPage(1);
  };

  const handleTanneryChange = (value) => {
    setSuccessMessage("");
    setTanneryId(value);
    setPage(1);
  };

  const handleStatusChange = (value) => {
    setSuccessMessage("");
    setStatus(value);
    setPage(1);
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) {
      return;
    }
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteError("");
      const response = await fetch(
        `${getBaseApiUrl()}/super-admin/tokens/${encodeURIComponent(deleteTarget.id)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      const payload = await parseResponsePayload(response);
      if (!response.ok) {
        throw new Error(getDeleteErrorMessage(response.status, payload));
      }

      setSuccessMessage(
        `Token ${deleteTarget.short_code !== "—" ? deleteTarget.short_code : deleteTarget.id} deleted.`
      );
      setDeleteTarget(null);
      void fetchTokens();
    } catch (error) {
      setDeleteError(error.message || "Unable to delete this token right now.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <section className="dashboard-card" id="super-admin-tokens">
      <div className="dashboard-users-list-header">
        <h2>All tokens</h2>
        <PrimaryButton type="button" onClick={() => void fetchTokens()} disabled={isLoading}>
          {isLoading ? "Refreshing..." : "Refresh"}
        </PrimaryButton>
      </div>
      <p className="dashboard-card-subtitle">
        Cross-tannery token list with the same search behaviour as token search. Tannery and checkout
        status update the list as soon as you change them; use Apply or Enter in the search box for
        the text search. Delete is only available for waiting tokens; checked-out tokens return HTTP
        409.
      </p>

      {successMessage ? (
        <p className="dashboard-success-message" role="status">
          {successMessage}
        </p>
      ) : null}

      {listError ? (
        <p className="dashboard-error-message" role="alert">
          {listError}
        </p>
      ) : null}

      {tanneriesError ? (
        <p className="dashboard-error-message" role="alert">
          {tanneriesError}
        </p>
      ) : null}

      <div className="modal-form" style={{ marginTop: "0.75rem", marginBottom: "1rem" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: "0.75rem 1rem",
            alignItems: "end",
          }}
        >
          <FormSelect
            id="super-admin-tokens-tannery"
            name="tannery_id"
            label="Tannery"
            value={tanneryId}
            onChange={(event) => handleTanneryChange(event.target.value)}
            options={tanneryOptions}
            placeholder={isLoadingTanneries ? "Loading tanneries..." : "All tanneries"}
            disabled={isLoadingTanneries}
          />
          <FormSelect
            id="super-admin-tokens-status"
            name="status"
            label="Checkout status"
            value={status}
            onChange={(event) => handleStatusChange(event.target.value)}
            options={STATUS_FILTER_OPTIONS}
            placeholder="Any status"
          />
          <FormInput
            id="super-admin-tokens-q"
            name="q"
            label="Search"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder="Code, name, phone, plate, zila…"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                applyFilters();
              }
            }}
          />
          <div className="form-group">
            <label htmlFor="super-admin-tokens-page-size">Page size</label>
            <select
              id="super-admin-tokens-page-size"
              className="score-filter-select"
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <span className="score-filter-label" style={{ display: "block", marginBottom: "0.35rem" }}>
              &nbsp;
            </span>
            <PrimaryButton type="button" onClick={applyFilters} disabled={isLoading}>
              Apply filters
            </PrimaryButton>
          </div>
        </div>
      </div>

      {isLoading ? <p>Loading tokens...</p> : null}

      {!isLoading && rows.length === 0 ? <p>No tokens match the current filters.</p> : null}

      {!isLoading && rows.length > 0 ? (
        <div>
          <div className="logs-pagination-toolbar" style={{ marginBottom: "0.75rem" }}>
            <p className="token-list-count">Showing {rangeLabel}</p>
          </div>

          <div className="dashboard-users-table-wrapper">
            <table className="dashboard-users-table">
              <thead>
                <tr>
                  <th scope="col">Short code</th>
                  <th scope="col">Tannery</th>
                  <th scope="col">Name</th>
                  <th scope="col">Phone</th>
                  <th scope="col">Plate</th>
                  <th scope="col">Zila</th>
                  <th scope="col">Elaqa</th>
                  <th scope="col">Status</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.short_code}</td>
                    <td>{row.tannery_name}</td>
                    <td>{row.name}</td>
                    <td>{row.phone_number}</td>
                    <td>{row.car_plate}</td>
                    <td>{row.zila}</td>
                    <td>{row.elaqa}</td>
                    <td>{row.status}</td>
                    <td>
                      {row.canDelete ? (
                        <button
                          type="button"
                          className="restriction-toggle-btn restriction-toggle-btn-restrict"
                          onClick={() => {
                            setDeleteError("");
                            setDeleteTarget(row);
                          }}
                        >
                          Delete
                        </button>
                      ) : (
                        <span className="score-filter-label">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="logs-pagination-toolbar">
            <div className="logs-pagination-controls">
              <PrimaryButton
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={!hasPrevPage || isLoading}
              >
                Previous
              </PrimaryButton>
              <span className="score-filter-label">
                Page {page}
                {totalPages != null ? ` of ${totalPages}` : ""}
              </span>
              <PrimaryButton
                type="button"
                onClick={() => setPage((prev) => prev + 1)}
                disabled={!hasNextPage || isLoading}
              >
                Next
              </PrimaryButton>
            </div>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="super-admin-delete-token-title"
          onClick={() => !isDeleting && setDeleteTarget(null)}
        >
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h2 id="super-admin-delete-token-title">Delete token</h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => !isDeleting && setDeleteTarget(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {deleteError ? (
              <p className="dashboard-error-message" role="alert">
                {deleteError}
              </p>
            ) : null}

            <p style={{ marginBottom: "1rem" }}>
              Permanently delete token{" "}
              <strong>{deleteTarget.short_code !== "—" ? deleteTarget.short_code : deleteTarget.id}</strong>
              {" from "}
              <strong>{deleteTarget.tannery_name}</strong>? This removes related livestock update logs
              and cannot be undone.
            </p>

            <div className="modal-form" style={{ flexDirection: "row", gap: "0.75rem" }}>
              <PrimaryButton type="button" onClick={() => !isDeleting && setDeleteTarget(null)}>
                Cancel
              </PrimaryButton>
              <button
                type="button"
                className="restriction-toggle-btn restriction-toggle-btn-restrict"
                onClick={() => void handleDelete()}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default SuperAdminTokensPage;
